import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtom, useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import BoltIcon from '@flipster/react/icon/generated/BoltIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack, vstack } from '@flipster/styled-system/patterns';
import { alert } from '@flipster/ui/Alert';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { SparkAuctionEvent } from '@flipster/utils/segment/events/spark-auction';

import { sparkAuctionBunja, sparkAuctionsBunja, sparkBunja } from '../_bunja';
import SparksInput from '../_misc/SparksInput';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onFinish: () => void;
}

const BidModal = ({ isOpen, onClose, onFinish }: Props) => {
  const {
    myCurrentBidAtom,
    availableSparksAtom,
    isFirstBidAtom,
    bidAmountAtom,
    myNewTotalBidAtom,
    amountExeedsAvailableSparksAtom,
    isValidAmountAtom,
    setBidAmountByPercent,
    bidAuction,
  } = useBunja(bidModalInnerBunja);

  const myCurrentBid = useAtomValue(myCurrentBidAtom);
  const availableSparks = useAtomValue(availableSparksAtom);
  const isFirstBid = useAtomValue(isFirstBidAtom);
  const [bidAmount, setBidAmount] = useAtom(bidAmountAtom);
  const myNewTotalBid = useAtomValue(myNewTotalBidAtom);
  const amountExeedsAvailableSparks = useAtomValue(amountExeedsAvailableSparksAtom);
  const isValidAmount = useAtomValue(isValidAmountAtom);

  return (
    <Modal isOpen={isOpen}>
      <Modal.Close onClose={onClose} />
      <Modal.Header css={hstack.raw({ gap: 8 })}>
        <BoltIcon size={24} />
        <Modal.Title label={isFirstBid ? 'Enter Sparks to bid' : 'Add Sparks to your current bid'} />
      </Modal.Header>
      <Modal.Content
        css={stack.raw({ pb: 60, gap: 24, textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}
      >
        <div className={stack({ gap: 8, textAlign: 'center' })}>
          <SparksInput value={bidAmount} onInput={setBidAmount} />
          {amountExeedsAvailableSparks && (
            <p className={css({ color: 'content.state.negative' })}>
              <FormattedMessage id='Autogen_common.r251126.s_Amount_exceeds_your_available_Sparks' />
            </p>
          )}
          <div
            className={vstack({
              color: 'content.default.level-2',
              '& span:nth-child(1)': { textStyle: 'Text/S/Regular' },
              '& [data-strong]': {
                textStyle: 'Text/M/Strong',
                color: 'content.default.level-5',
                '& span:nth-child(1)': { textStyle: 'Text/S/Strong' },
              },
            })}
          >
            {!isFirstBid && !isValidAmount && (
              <div className={hstack({ gap: 4 })}>
                <span>
                  <FormattedMessage id='Autogen_common.r251126.s_Your_current_bid' />
                </span>
                <span>
                  {numberFormatter(myCurrentBid)}
                  <FormattedMessage id='Autogen_common.r251126.s_Sparks' />
                </span>
              </div>
            )}
            {!isFirstBid && isValidAmount && (
              <div data-strong className={hstack({ gap: 4 })}>
                <span>
                  <FormattedMessage id='Autogen_common.r251126.s_Your_new_total_bid' />
                </span>
                <span>
                  {numberFormatter(myNewTotalBid)}
                  <FormattedMessage id='Autogen_common.r251126.s_Sparks' />
                </span>
              </div>
            )}
            {(isFirstBid || !isValidAmount) && (
              <div className={hstack({ gap: 4 })}>
                <span>
                  <FormattedMessage id='Trade.r250401.Available_colon' />
                </span>
                <span>
                  {numberFormatter(availableSparks)}
                  <FormattedMessage id='Autogen_common.r251126.s_Sparks' />
                </span>
              </div>
            )}
          </div>
        </div>
        <BidButtons onClick={setBidAmountByPercent} />
        <InfoMessage />
      </Modal.Content>
      <Modal.Footer>
        <Button
          label={isFirstBid ? 'Place bid' : 'Add'}
          disabled={!isValidAmount}
          withSpinner
          onClick={async () => {
            await bidAuction();
            onFinish();
          }}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default BidModal;

const bidModalInnerBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const sparkBunjaInstance = bunja.use(sparkBunja);
  const sparkAuctionsBunjaInstance = bunja.use(sparkAuctionsBunja);
  const sparkAuctionBunjaInstance = bunja.use(sparkAuctionBunja);
  const { mySparksAtom: availableSparksAtom } = sparkBunjaInstance;
  const { auctionId, isFirstBidAtom, myBidAmountAtom: myCurrentBidAtom } = sparkAuctionBunjaInstance;

  const bidAmountAtom = atom(0);

  const myNewTotalBidAtom = atom((get) => {
    const bidAmount = get(bidAmountAtom);
    const myCurrentBid = get(myCurrentBidAtom);
    return bidAmount + myCurrentBid;
  });

  const amountExeedsAvailableSparksAtom = atom((get) => {
    const bidAmount = get(bidAmountAtom);
    const availableSparks = get(availableSparksAtom);
    return bidAmount > availableSparks;
  });

  const isValidAmountAtom = atom((get) => {
    const bidAmount = get(bidAmountAtom);
    const amountExeedsAvailableSparks = get(amountExeedsAvailableSparksAtom);
    if (bidAmount <= 0) return false;
    if (amountExeedsAvailableSparks) return false;
    return true;
  });

  function setBidAmountByPercent(percent: number) {
    const availableSparks = store.get(availableSparksAtom);
    const bidAmount = Math.round((availableSparks * percent) / 100);
    store.set(bidAmountAtom, bidAmount);
  }

  async function bidAuction() {
    try {
      const isFirstBid = store.get(isFirstBidAtom);
      const availableSparks = store.get(availableSparksAtom);
      const amount = store.get(myNewTotalBidAtom);
      const myCurrentBid = store.get(myCurrentBidAtom);
      if (isFirstBid) {
        segment.track(SparkAuctionEvent.OrderFormPlaceBidClick, {
          available_sparks: availableSparks,
          num_sparks: amount,
        });
      } else {
        segment.track(SparkAuctionEvent.OrderFormAddBidClick, {
          available_sparks: availableSparks,
          num_sparks: amount,
          current_bid: myCurrentBid,
        });
      }
      await sparkAuctionsBunjaInstance.bidAuction(auctionId, amount);
      const message = isFirstBid ? 'Your bid has been placed.' : 'Your bid has been increased.';
      snackbar.success({ message });
    } catch (error) {
      alert.error(error);
    }
  }

  return {
    myCurrentBidAtom,
    availableSparksAtom,
    isFirstBidAtom,
    bidAmountAtom,
    myNewTotalBidAtom,
    amountExeedsAvailableSparksAtom,
    isValidAmountAtom,
    setBidAmountByPercent,
    bidAuction,
  };
});
bidModalInnerBunja.debugLabel = 'bidModalInnerBunja';

interface BidButtonsProps {
  onClick: (percent: number) => void;
}
const BidButtons = ({ onClick }: BidButtonsProps) => {
  return (
    <div
      className={hstack({
        gap: 6,
        '& > button': {
          py: 8,
          flex: '1 0 0',
          borderRadius: 4,
          textStyle: 'Text/S/Strong',
          bg: 'background.default.level-3',
          color: 'content.default.level-2',
          '&:active': { color: 'content.default.level-5' },
          '&:not(:disabled):focus': {
            pos: 'relative',
            _before: {
              pos: 'absolute',
              inset: -2,
              content: '""',
              border: '1px solid {colors.border.accent.info}',
              borderRadius: 6,
            },
          },
        },
      })}
    >
      <button onClick={() => onClick(25)}>25%</button>
      <button onClick={() => onClick(50)}>50%</button>
      <button onClick={() => onClick(75)}>75%</button>
      <button onClick={() => onClick(100)}>100%</button>
    </div>
  );
};

const InfoMessage = () => {
  return (
    <div
      className={hstack({
        gap: 8,
        textStyle: 'Text/M/Regular',
        color: 'content.default.level-2',
      })}
    >
      <InformationCircleIcon size={16} className={css({ flexShrink: 0 })} />
      <span>
        <FormattedMessage id='Autogen_common.r251126.s_If_you_don_t_win_your_Sparks_will_be_returned' />
      </span>
    </div>
  );
};
